import { useState } from "react";
import {
  deleteCoAuthorsByPaperId,
  fetchRegistrationById,
  saveCoAuthor,
  saveConferenceRegistration,
  savePaperSubmission,
  uploadRegistrationFile,
} from "../api/conferenceRegistrationApi";
import {
  buildPaperFileUploadPayload,
  buildStudentIdCardUploadPayload,
} from "../payloads/fileUploadPayloads";
import { savePaperCoAuthors } from "../registration/coAuthorPersistence";
import { buildAdditionalFeesForSave } from "../payloads/registrationPayloadBuilders";
import {
  buildPaperSubmissionPayload,
  hasRequiredPaperSubmissionData,
  resolvePaperStatus,
  shouldSavePaperSubmission,
} from "../payloads/paperSubmissionPayloads";
import { validateBasicSaveContact } from "../registration/registrationValidation";
import { getReloadedFeeCollections } from "../registration/registrationSync";

export const useRegistrationSave = ({
  registrationBaseUrl,
  conferenceRegistrationUrl,
  formData,
  setFormData,
  setStep,
  setErrors,
  validateField,
  buildConferenceRegistrationPayload,
  accompanyingPersons,
  accompanyingPersonFee,
  selectedFocusedSessions,
  focusedSessions,
  focusedSessionFees,
  setRegistrationJustUpdated,
  step,
  isAbstractRequired,
  wantsToSubmitAbstract,
  abstractStepNumber,
  submissionMethod,
  paperTitle,
  selectedTrack,
  abstractText,
  paperFile,
  presentationMode,
  paperSubmissionAction,
  paperApprovalStatus,
  existingPaperId,
  coAuthors,
  setExistingPaperId,
  setSubmittedPapers,
  setHasPaperSubmitted,
  setPaperApprovalStatus,
  syncAccompanyingPersonsWithReloadedRegistration,
}) => {
  const [saving, setSaving] = useState(false);

  const saveStep = async (paperActionOverride = null) => {
    if (formData.registrationLookupPending || formData.registrationLookupError) {
      alert("Please reload and verify your registration before saving.");
      return false;
    }
    const contactValidation = validateBasicSaveContact({
      formData,
      validateField,
    });

    if (contactValidation.mobileError !== null) {
      setErrors((prev) => ({ ...prev, mobile: contactValidation.mobileError }));
    }

    if (!contactValidation.isValid) {
      alert("Please correct the highlighted contact details before saving.");
      setStep(1);
      return false;
    }

    try {
      setSaving(true);
      const additionalFeesArray = buildAdditionalFeesForSave({
        formData,
        accompanyingPersons,
        accompanyingPersonFee,
        selectedFocusedSessions,
        focusedSessions,
        focusedSessionFees,
      });

      const conferenceRegistrationData = buildConferenceRegistrationPayload({
        additionalFees: additionalFeesArray,
        includeGender: true,
        includeIsaMembershipNumber: true,
      });

      const isUpdate = formData.id || formData.regId;
      const registrationIdForUpdate = formData.id || formData.regId;

      const saveResult = await saveConferenceRegistration({
        conferenceRegistrationUrl,
        registrationId: isUpdate ? registrationIdForUpdate : null,
        payload: conferenceRegistrationData,
      });

      if (!saveResult.ok) {
        const txt = saveResult.errorText;
        console.error("Save failed:", {
          status: saveResult.status,
          statusText: saveResult.statusText,
          response: txt,
          conference: conferenceRegistrationData.conference,
        });
        alert(`Save failed: ${txt}`);
        return false;
      }

      const saved = saveResult.data;
      setRegistrationJustUpdated(true);

      if (saved.registrationId) {
        setFormData((prev) => ({ ...prev, id: saved.registrationId }));
      }
      if (saved.dbId || saved.id) {
        setFormData((prev) => ({ ...prev, regId: saved.dbId || saved.id }));
      }

      if (
        (saved.dbId || saved.id) &&
        formData.studentIdCard &&
        formData.studentIdCard.startsWith("data:")
      ) {
        try {
          const registrationId = saved.dbId || saved.id;
          const fileData = buildStudentIdCardUploadPayload({
            studentIdCard: formData.studentIdCard,
            studentIdCardName: formData.studentIdCardName,
            studentIdCardType: formData.studentIdCardType,
          });

          const fileUploadResult = await uploadRegistrationFile({
            registrationBaseUrl,
            registrationId,
            fileType: "id-card",
            payload: {
              fileType: fileData.fileType,
              fileData: fileData.fileData,
              fileName: fileData.fileName,
              contentType: fileData.contentType,
              fileSize: fileData.fileSize,
            },
          });

          if (!fileUploadResult.ok) {
            console.error(
              "Failed to upload student ID card:",
              fileUploadResult.errorText
            );
            alert("Warning: File upload failed. Please try uploading again.");
          } else {
            setFormData((prev) => ({
              ...prev,
              studentIdCard: "DATABASE",
              studentIdCardUploaded: true,
            }));
          }
        } catch (fileErr) {
          console.error("Error uploading student ID card:", fileErr);
        }
      }

      const hasRequiredPaperData = hasRequiredPaperSubmissionData({
        paperTitle,
        selectedTrack,
        submissionMethod,
        abstractText,
        paperFile,
      });

      const shouldSavePaper = shouldSavePaperSubmission({
        hasRequiredPaperData,
        step,
        isAbstractRequired,
        wantsToSubmitAbstract,
        abstractStepNumber,
      });

      if (shouldSavePaper) {
        try {
          const registrationId = saved.dbId || saved.id;

          if (paperFile) {
            const fileData = await buildPaperFileUploadPayload(paperFile);
            const fileUploadResult = await uploadRegistrationFile({
              registrationBaseUrl,
              registrationId,
              fileType: "paper",
              payload: {
                fileType: fileData.fileType,
                fileData: fileData.fileData,
                fileName: fileData.fileName,
                contentType: fileData.contentType,
                fileSize: fileData.fileSize,
              },
            });

            if (!fileUploadResult.ok) {
              throw new Error("Failed to upload paper file");
            }
          }

          const { paperStatus } = resolvePaperStatus({
            paperActionOverride,
            paperSubmissionAction,
            paperApprovalStatus,
            submissionMethod,
          });

          const paperData = buildPaperSubmissionPayload({
            registrationId,
            selectedTrack,
            paperTitle,
            abstractText,
            paperStatus,
            presentationMode,
          });

          let savedPaper;
          if (existingPaperId) {
            const paperResult = await savePaperSubmission({
              registrationBaseUrl,
              paperId: existingPaperId,
              payload: paperData,
            });

            if (!paperResult.ok) {
              throw new Error(
                `Failed to update paper submission: ${paperResult.errorText}`
              );
            }

            savedPaper = paperResult.data;
            setSubmittedPapers((currentPapers) =>
              currentPapers.map((paper) =>
                String(paper.id) === String(savedPaper.id) ? savedPaper : paper
              )
            );
          } else {
            const paperResult = await savePaperSubmission({
              registrationBaseUrl,
              payload: paperData,
            });

            if (!paperResult.ok) {
              throw new Error(
                `Failed to save paper submission: ${paperResult.errorText}`
              );
            }

            savedPaper = paperResult.data;
            setExistingPaperId(savedPaper.id);
            setSubmittedPapers((currentPapers) => [
              ...currentPapers,
              savedPaper,
            ]);
          }

          await savePaperCoAuthors({
            registrationBaseUrl,
            savedPaper,
            existingPaperId,
            coAuthors,
            deleteCoAuthorsByPaperId,
            saveCoAuthor,
          });

          setHasPaperSubmitted(true);
          setPaperApprovalStatus(savedPaper?.status || paperStatus);
        } catch (paperErr) {
          console.error("Error saving paper submission:", paperErr);
          alert(
            `Warning: Paper submission failed. ${paperErr.message}\nPlease try again from the Paper/Abstract step.`
          );
        }
      }

      if (saved.dbId || saved.id) {
        try {
          const reloadedReg = await fetchRegistrationById({
            conferenceRegistrationUrl,
            registrationId: saved.dbId || saved.id,
          });

          if (reloadedReg && Array.isArray(reloadedReg.additionalFees)) {
            const reloadedFees = getReloadedFeeCollections(reloadedReg);
            setFormData((prev) => ({
              ...prev,
              additionalFees: reloadedFees.additionalFees,
              zeroPayments: reloadedFees.zeroPayments,
            }));

            syncAccompanyingPersonsWithReloadedRegistration(reloadedReg);
          }
        } catch (reloadErr) {
          console.error("Error reloading registration:", reloadErr);
        }
      }

      return true;
    } catch (err) {
      console.error("Save error:", err);
      alert(`Could not save. Error: ${err.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    saveStep,
  };
};
